import { useQuery } from "@tanstack/react-query";
import type { AuditEventDTO, ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Chip, EmptyState, Panel, type Tone } from "@/design/primitives";
import { DataTable, type Column } from "@/design/DataTable";
import { relativeTime } from "@/lib/format";

const actorTone: Record<string, Tone> = {
  HUMAN: "amber",
  AI: "cyan",
  WORKER: "green",
  SYSTEM: "violet",
};

/** Projektový audit — týž vzor tabulky jako globální Audit, filtrovaný na projekt. */
export function ProjectAuditTab({ project }: { project: ProjectDetailDTO }) {
  const query = useQuery({
    queryKey: ["audit", "project", project.id, "full"],
    queryFn: () => api.audit({ projectId: project.id, limit: 100 }),
  });
  const events = query.data?.events ?? [];

  const columns: Column<AuditEventDTO>[] = [
    {
      key: "ts",
      label: cs.audit.time,
      width: "110px",
      cell: (e) => <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(e.ts)}</span>,
    },
    {
      key: "actor",
      label: cs.audit.actor,
      width: "100px",
      cell: (e) => <Chip tone={actorTone[e.actorType] ?? "dim"}>{e.actorType}</Chip>,
    },
    {
      key: "action",
      label: cs.audit.action,
      width: "minmax(180px, 220px)",
      primary: true,
      cell: (e) => <span style={{ letterSpacing: ".04em" }}>{e.action}</span>,
    },
    {
      key: "summary",
      label: cs.audit.detail,
      cell: (e) => (
        <>
          {e.summary}
          {e.reason ? <span style={{ color: "var(--text-tertiary)" }}> · {e.reason}</span> : null}
        </>
      ),
    },
    {
      key: "link",
      label: cs.audit.link,
      width: "minmax(110px, 140px)",
      cell: (e) => <span style={{ color: "var(--text-tertiary)" }}>{e.link ?? ""}</span>,
    },
  ];

  return (
    <Panel className="mt-4 p-4">
      <DataTable
        columns={columns}
        rows={events}
        rowKey={(e) => e.id}
        stackBelow={1024}
        empty={<EmptyState title={cs.audit.empty} />}
      />
    </Panel>
  );
}
